import React, { useState } from "react";
import CloseIcon from "../../../assets/crossIcon.svg";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

const ImprovementFeedbackForm = ({ onClose, caseId, AllCases }) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    profitGenerated: "",
    selectedCases: ["Rejection saved", "Process improved"],
    underBillingCost: "",
    timeSavedHours: "",
    remarks: "",
    profitIncreasePercent: "",
  });

  const navigate = useNavigate();

  const options = [
    { label: "Rejection saved", hasTextbox: false },
    {
      label: "Under billing saved",
      hasTextbox: true,
      textboxKey: "underBillingCost",
    },
    { label: "Process improved", hasTextbox: false },
    {
      label: "Time saved of clinical doctors",
      hasTextbox: true,
      textboxKey: "timeSavedHours",
    },
  ];

  const handleCheckboxChange = (label) => {
    setFormData((prev) => {
      const alreadySelected = prev.selectedCases.includes(label);
      return {
        ...prev,
        selectedCases: alreadySelected
          ? prev.selectedCases.filter((item) => item !== label)
          : [...prev.selectedCases, label],
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const subMasterStageData = {
        hsaStage: "Claim Received",
        commandCeneterStage: "Claim Received",
        nextStepUser: "NA",
        caseDetails: [{ query: "", queryResponse: "" }],
      };

      const summaryDetail = {
        profitGenerated: formData.profitGenerated,
        selectedCases: formData.selectedCases,
        underBillingCost: formData.underBillingCost,
        timeSavedHours: formData.timeSavedHours,
        remarks: formData.remarks,
        profitIncreasePercent: formData.profitIncreasePercent,
      };

      const stageResponse = await CaseServices.updateSubMasterStage(
        caseId,
        subMasterStageData
      );
      window.alert(stageResponse.data.message);

      await CaseServices.updateCase({ SummaryDetail: summaryDetail }, caseId);

      onClose();
      AllCases && (await AllCases());
      navigate("/dashboard/ActiveCases");
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-white w-[80%] max-w-5xl rounded-2xl shadow-2xl p-10 border border-gray-100 animate-fadeIn relative">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          💡 Improvement / Claim Feedback
        </h2>
        <button
          onClick={onClose}
          className="cursor-pointer bg-transparent border-none p-0 m-0 hover:scale-105 transition-transform"
          disabled={isSubmitting}
        >
          <img
            src={CloseIcon}
            // height={30}
            // width={30}
            alt="close-icon"
            className="opacity-80 hover:opacity-100"
            height={28}
            width={28}
          />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Profit generate */}
        <div>
          <label className="block text-lg font-semibold text-gray-800 mb-2">
            Profit Generated in Case (₹)
          </label>
          <input
            type="text"
            name="profitGenerated"
            value={formData.profitGenerated}
            onChange={handleChange}
            placeholder="Enter profit amount"
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-black focus:border-black transition-all"
          />
        </div>

        {/* Cases Under */}
        <div>
          <label className="block text-lg font-semibold text-gray-800 mb-3">
            Case Category
          </label>
          <div className="grid grid-cols-2 gap-5">
            {options.map((opt) => (
              <div
                key={opt.label}
                className="flex flex-col border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all bg-gray-50"
              >
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.selectedCases.includes(opt.label)}
                    onChange={() => handleCheckboxChange(opt.label)}
                    className="accent-black w-4 h-4"
                  />
                  <span className="text-gray-900 text-base font-medium">
                    {opt.label}
                  </span>
                </label>
                {opt.hasTextbox &&
                  formData.selectedCases.includes(opt.label) && (
                    <input
                      type="text"
                      name={opt.textboxKey}
                      value={formData[opt.textboxKey]}
                      onChange={handleChange}
                      placeholder={
                        opt.textboxKey === "underBillingCost"
                          ? "Enter cost of billing saved"
                          : "Enter time saved (hours)"
                      }
                      className="mt-3 border border-gray-300 rounded-md px-3 py-1.5 text-gray-800 focus:ring-2 focus:ring-black focus:border-black transition-all"
                    />
                  )}
              </div>
            ))}
          </div>
        </div>

        {/* Remarks */}
        <div>
          <label className="block text-lg font-semibold text-gray-800 mb-2">
            Summary Remarks
          </label>
          <textarea
            name="remarks"
            rows={3}
            value={formData.remarks}
            onChange={handleChange}
            placeholder="Write brief remarks about this case improvement..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-black focus:border-black transition-all resize-none"
          />
        </div>

        {/* Profit Increase Percentage */}
        <div>
          <label className="block text-lg font-semibold text-gray-800 mb-2">
            Profit Increase Percentage (%)
          </label>
          <input
            type="number"
            name="profitIncreasePercent"
            value={formData.profitIncreasePercent}
            onChange={handleChange}
            placeholder="Enter profit % (0 - 100)"
            min="0"
            max="100"
            className="w-1/2 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-black focus:border-black transition-all"
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-4">
          {isSubmitting ? (
            <CircularProgress size={28} />
          ) : (
            <button
              type="submit"
              className="bg-black text-white text-base font-semibold px-8 py-2.5 rounded-lg hover:bg-gray-900 shadow-md hover:shadow-lg transition-all"
            >
              SUBMIT FEEDBACK
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ImprovementFeedbackForm;
